import { call, put, select, takeLatest } from 'redux-saga/effects'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'

import {
  submitRequest,
  submitSuccess,
  submitFailure,
  fetchProvidersRequest,
  fetchProvidersSuccess,
  fetchProvidersFailure,
} from './podUpdaterSlice'

import {
  selectPodUpdaterMode,
  selectPodUpdaterPodsText, selectPodUpdaterProviderUuid
} from './podUpdaterSelectors'

import { mapProvidersToSelectOptions, parsePodsList, canSubmitPodUpdater } from './utils/podUpdaterFormatters'

export const podUpdaterActions = {
  FETCH_PROVIDERS: 'DEVTOOLS_NEW/POD_UPDATER/FETCH_PROVIDERS',
  SUBMIT: 'DEVTOOLS_NEW/POD_UPDATER/SUBMIT',
}

function* fetchProvidersSaga({ enqueue, t, payload }) {
  try {
    yield put(fetchProvidersRequest())

    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.devTools?.providersList
    if (!url) {
      const message = t?.('pod_updater.errors.missing_providers_url') || 'Missing providers url'
      yield put(fetchProvidersFailure(message))
      if (enqueue) {
        yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
      }
      return
    }

    const businessType = payload?.businessType
    const country = payload?.country

    if (!businessType || !country) {
      const message = t?.('pod_updater.errors.missing_vertical_or_country') || 'Missing businessType or country'
      yield put(fetchProvidersFailure(message))
      if (enqueue) yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
      return
    }

    const endpoint = String(url)
      .replace('*businessType*', businessType)
      .replace('*country*', country)

    const result = yield call(callApi, {
      endpoint,
      method: 'GET',
      showErrorSnackbar: false,
    })

    const providers = mapProvidersToSelectOptions(result?.providers || [])
    yield put(fetchProvidersSuccess(providers))
  } catch (error) {
    const message = error?.response?.data?.error || error?.message || 'Error fetching providers'
    yield put(fetchProvidersFailure(message))
  }
}

function* submitPodUpdaterSaga({ enqueue, t }) {
  yield put(submitRequest())

  try {
    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.devTools?.contractsStatusUpdateByPods
    if (!url) {
      const message = t?.('pod_updater.errors.missing_url') || 'Missing podUpdaterUrl'
      yield put(submitFailure({ error: message }))
      if (enqueue) {
        yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
      }
      return
    }

    const mode = yield select(selectPodUpdaterMode)
    const providerUuid = yield select(selectPodUpdaterProviderUuid)
    const podsText = yield select(selectPodUpdaterPodsText)

    if (!canSubmitPodUpdater({ providerUuid, podsText })) {
      const message =
        t?.('pod_updater.errors.invalid_form') ||
        'Please select a provider and enter at least one PDL/PCE'
      yield put(submitFailure({ error: message }))
      if (enqueue) yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.WARNING, message })
      return
    }

    const result = yield call(callApi, {
      endpoint: url,
      method: 'POST',
      payload: {
        providerUuid: providerUuid,
        action: mode,
        pods: parsePodsList(podsText),
      },
      showErrorSnackbar: false,
    })

    const message = result?.message || ''
    yield put(submitSuccess({ message }))

    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: message || (t?.('pod_updater.success') || 'Batch launched'),
      })
    }
  } catch (error) {
    const message = error?.response?.data?.error || error?.message || 'Unknown error'
    yield put(submitFailure({ error: message }))
    if (enqueue) {
      yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
    }
  }
}

export default function* podUpdaterSagas() {
  yield takeLatest(podUpdaterActions.FETCH_PROVIDERS, fetchProvidersSaga)
  yield takeLatest(podUpdaterActions.SUBMIT, submitPodUpdaterSaga)
}
